import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LanguageSwitcherComponent } from '../language-switcher/language-switcher';
import { AuthService } from '../../../core/services/auth';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, TranslateModule, LanguageSwitcherComponent],
  template: `
    <header class="bg-enduro-black text-white shadow-md sticky top-0 z-40">
      <div class="container mx-auto px-4 py-4 flex justify-between items-center relative">
        <!-- Logo -->
        <a routerLink="/" class="flex items-center z-50">
          <img src="assets/logo.png" alt="Tour Enduro Logo" class="h-10 md:h-12 w-auto">
        </a>

        <!-- Desktop Nav -->
        <nav class="hidden md:flex items-center gap-6 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <a routerLink="/" routerLinkActive="text-enduro-orange" [routerLinkActiveOptions]="{exact: true}" class="hover:text-enduro-orange transition-colors">{{ 'NAV.HOME' | translate }}</a>
          <a routerLink="/events" routerLinkActive="text-enduro-orange" class="hover:text-enduro-orange transition-colors">{{ 'NAV.EVENTS' | translate }}</a>
          <!-- <a routerLink="/courses" routerLinkActive="text-enduro-orange" class="hover:text-enduro-orange transition-colors">{{ 'NAV.COURSES' | translate }}</a> -->
          <a routerLink="/membership" routerLinkActive="text-enduro-orange" class="hover:text-enduro-orange transition-colors">{{ 'NAV.MEMBERSHIP' | translate }}</a>
          <a routerLink="/contact" routerLinkActive="text-enduro-orange" class="hover:text-enduro-orange transition-colors">{{ 'NAV.CONTACT' | translate }}</a>
        </nav>

        <!-- Utils & Hamburger -->
        <div class="flex items-center gap-4 z-50">
          <app-language-switcher class="hidden md:block"></app-language-switcher>
          
          @if (auth.isAuthenticated()) {
            <div class="relative hidden md:inline-block">
                <button (click)="toggleUserMenu()" class="flex items-center gap-2 bg-enduro-rust px-3 py-1 rounded text-sm hover:bg-opacity-80 transition-colors">
                    Admin
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transform transition-transform" [class.rotate-180]="isUserMenuOpen()" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                @if (isUserMenuOpen()) {
                    <!-- Backdrop to close on click outside -->
                    <div class="fixed inset-0 z-40 cursor-default" (click)="closeUserMenu()"></div>
                    
                    <div class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 animate-fade-in border border-gray-100">
                        <a routerLink="/admin/dashboard" (click)="closeUserMenu()" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-enduro-orange transition-colors">
                            Dashboard
                        </a>
                        <button (click)="logout()" class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100">
                            Esci
                        </button>
                    </div>
                }
            </div>
          }

          <!-- Hamburger Button -->
          <button (click)="toggleMenu()" class="md:hidden text-white focus:outline-none p-2" aria-label="Menu">
            <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              @if (isMenuOpen()) {
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
              } @else {
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
              }
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Menu Overlay -->
      @if (isMenuOpen()) {
        <div class="fixed inset-0 bg-enduro-black/95 z-40 pt-24 px-6 md:hidden flex flex-col gap-6 animate-fade-in text-center">
           <a routerLink="/" (click)="closeMenu()" routerLinkActive="text-enduro-orange" [routerLinkActiveOptions]="{exact: true}" class="text-2xl font-bold hover:text-enduro-orange transition-colors border-b border-gray-800 pb-2">{{ 'NAV.HOME' | translate }}</a>
           <a routerLink="/events" (click)="closeMenu()" routerLinkActive="text-enduro-orange" class="text-2xl font-bold hover:text-enduro-orange transition-colors border-b border-gray-800 pb-2">{{ 'NAV.EVENTS' | translate }}</a>
           <!-- <a routerLink="/courses" (click)="closeMenu()" routerLinkActive="text-enduro-orange" class="text-2xl font-bold hover:text-enduro-orange transition-colors border-b border-gray-800 pb-2">{{ 'NAV.COURSES' | translate }}</a> -->
           <a routerLink="/membership" (click)="closeMenu()" routerLinkActive="text-enduro-orange" class="text-2xl font-bold hover:text-enduro-orange transition-colors border-b border-gray-800 pb-2">{{ 'NAV.MEMBERSHIP' | translate }}</a>
           <a routerLink="/contact" (click)="closeMenu()" routerLinkActive="text-enduro-orange" class="text-2xl font-bold hover:text-enduro-orange transition-colors border-b border-gray-800 pb-2">{{ 'NAV.CONTACT' | translate }}</a>
           
           <!-- Mobile Utils -->
           <div class="mt-4 flex flex-col items-center gap-6">
             <div class="flex items-center gap-2 border-b border-gray-800 pb-4 w-full justify-center">
               <span class="text-gray-400">Lingua:</span>
               <app-language-switcher></app-language-switcher>
             </div>
             
             @if (auth.isAuthenticated()) {
               <a routerLink="/admin/dashboard" (click)="closeMenu()" class="text-enduro-rust font-bold text-xl">Admin Dashboard</a>
               <button (click)="logout()" class="text-red-500 font-bold text-lg mt-2">Esci (Logout)</button>
             }
           </div>
        </div>
      }
    </header>
  `
})
export class HeaderComponent {
  auth = inject(AuthService);
  isMenuOpen = signal(false);

  toggleMenu() {
    this.isMenuOpen.update(v => !v);
  }

  closeMenu() {
    this.isMenuOpen.set(false);
  }

  // User Dropdown Logic
  isUserMenuOpen = signal(false);

  toggleUserMenu() {
    this.isUserMenuOpen.update(v => !v);
  }

  closeUserMenu() {
    this.isUserMenuOpen.set(false);
  }

  logout() {
    this.closeMenu();
    this.closeUserMenu();
    this.auth.logout();
  }
}
